import React, { useMemo, useState } from "react";
import { apiFetch } from "../../../../apiFetch";
import "./UserLookup.css";

const UserLookup = () => {
    const [table, setTable] = useState("allusers"); // allusers | player | agent | scout | club
    const [searchTerm, setSearchTerm] = useState("");
    const [searchBy, setSearchBy] = useState("all"); // all | fullName | username | profileSlug | loginEmail | id
    const [limit, setLimit] = useState(25);

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    // track which cards are expanded (details)
    const [expandedMap, setExpandedMap] = useState({}); // { [id]: boolean }

    // ✅ ADMIN EDIT STATES
    const [editOpen, setEditOpen] = useState(false);
    const [editLoading, setEditLoading] = useState(false);
    const [editSaving, setEditSaving] = useState(false);
    const [editError, setEditError] = useState("");
    const [editingRow, setEditingRow] = useState(null); // normalized row
    const [editRecord, setEditRecord] = useState(null); // full fetched record (player/agent/scout/club)
    const [editForm, setEditForm] = useState({}); // edited payload

    const toggleExpanded = (id) => {
        setExpandedMap((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const tableLabel = useMemo(() => {
        switch (table) {
            case "player":
                return "PlayerData";
            case "agent":
                return "AgentData";
            case "scout":
                return "ScoutData";
            case "club":
                return "ClubData";
            default:
                return "AllUsers";
        }
    }, [table]);

    const placeholder = useMemo(() => {
        switch (searchBy) {
            case "fullName":
                return "Search by full name (e.g., John Smith)";
            case "username":
                return "Search by username (e.g., megavolto)";
            case "profileSlug":
                return "Search by profileSlug (e.g., john-smith-2004)";
            case "loginEmail":
                return "Search by loginEmail (e.g., user@gmail.com)";
            case "id":
                return "Search by ID (e.g., 11)";
            default:
                return "Search by name, username, profileSlug, loginEmail, or ID...";
        }
    }, [searchBy]);

    const normalize = (r) => ({
        id: r.id,
        roleId: r.role_id ?? r.roleId ?? null, // ✅ for AllUsers results
        fullName: r.fullName ?? r.full_name ?? null,
        username: r.username ?? null,
        profileSlug: r.profileSlug ?? r.profile_slug ?? null,
        loginEmail: r.loginEmail ?? r.login_email ?? null,
        role: r.role ?? null,
        isDisabled: r.isDisabled ?? r.is_disabled ?? null,
        raw: r,
    });

    const searchUsers = async () => {
        const term = searchTerm.trim();
        if (!term) return;

        try {
            setLoading(true);
            setError("");

            const res = await apiFetch(
                `/api/admin/user-lookup?table=${encodeURIComponent(table)}&search=${encodeURIComponent(
                    term
                )}&limit=${encodeURIComponent(limit)}&searchBy=${encodeURIComponent(searchBy)}`,
                { method: "GET" }
            );

            if (!res.ok) throw new Error(await res.text());

            const data = await res.json();
            const list = Array.isArray(data) ? data : [];

            setRows(list.map(normalize));
            setExpandedMap({});
        } catch (err) {
            console.error("User lookup failed:", err);
            setError("Failed to search users.");
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    const onKeyDown = (e) => {
        if (e.key === "Enter") searchUsers();
    };

    // helper to render a label/value row
    const Field = ({ label, value }) => (
        <div className="UL__metaItem">
            <span className="UL__metaLabel">{label}</span>
            <span className="UL__metaValue">{value ?? "N/A"}</span>
        </div>
    );

    const formatValue = (v) => {
        if (v === null || v === undefined) return "N/A";
        if (typeof v === "boolean") return String(v);
        if (typeof v === "object") {
            try {
                return JSON.stringify(v);
            } catch {
                return "[object]";
            }
        }
        return String(v);
    };

    // ✅ Determine target entity + id for admin GET/PUT
    const resolveTarget = (row) => {
        const t = (table || "").toLowerCase();
        const rRole = (row?.role || "").toLowerCase();

        // When searching AllUsers, row.role + row.roleId (role_id) are authoritative
        if (t === "allusers") {
            const role = rRole;
            const id = row?.roleId;
            return { role, id };
        }

        // When searching a specific table directly, row.id is the PK in that table
        if (t === "player") return { role: "player", id: row?.id };
        if (t === "agent") return { role: "agent", id: row?.id };
        if (t === "scout") return { role: "scout", id: row?.id };
        if (t === "club") return { role: "club", id: row?.id };

        // fallback
        return { role: rRole || "unknown", id: row?.id };
    };

    // 🔒 Mirror backend blocked keys (no ripple)
    // NOTE: You explicitly said role/username/profileSlug should NOT change.
    const BLOCKED_KEYS = useMemo(
        () =>
            new Set([
                "id",

                // identity
                "firebaseUid",
                "firebase_uid",

                // role mapping
                "role",
                "roleId",
                "role_id",

                // slug/handle identity
                "username",
                "profileSlug",
                "profile_slug",

                // timestamps
                "createdAt",
                "updatedAt",
                "created_at",
                "updated_at",
            ]),
        []
    );

    // ✅ Model-informed type hints (from your Sequelize models)
    const MODEL_TYPE_HINTS = useMemo(() => {
        const DATE_KEYS = new Set([
            // common date-only fields across your models
            "dob",
            // common datetime fields
            "lastSyncedAt",
            "lastVideoPublishedAt",
            "createdAt",
            "updatedAt",
        ]);

        const BOOL_KEYS = new Set([
            // PlayerData
            "canEditTransfermarkt",
            "parentConsentProvided",
            "isDiscoverable",
            // Agent/Scout/Club
            "isVerified",
            // AllUsers
            "isDisabled",
            "betaAccess",
            "isVerified",
        ]);

        const NUMBER_KEYS = new Set([
            // PlayerData / AgentData / ScoutData
            "age",
            "yearsOfScouting",
        ]);

        // Keep it generic but safe:
        return { DATE_KEYS, BOOL_KEYS, NUMBER_KEYS };
    }, []);

    const humanizeKey = (k) => {
        if (!k) return "";
        const withSpaces = k.replace(/_/g, " ").replace(/([a-z0-9])([A-Z])/g, "$1 $2").trim();
        return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
    };

    const getEditableKeysFromRecord = (rec) => {
        if (!rec || typeof rec !== "object") return [];
        return Object.keys(rec)
            .filter((k) => !BLOCKED_KEYS.has(k))
            .sort((a, b) => a.localeCompare(b));
    };

    const looksLikeISODateOnly = (v) => typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v);
    const looksLikeISODateTime = (v) =>
        typeof v === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(v);

    const detectFieldType = (key, value) => {
        const k = String(key || "");
        const lower = k.toLowerCase();

        // ✅ Use model hints FIRST (important when current DB value is null)
        if (MODEL_TYPE_HINTS.BOOL_KEYS.has(k) || MODEL_TYPE_HINTS.BOOL_KEYS.has(lower)) return "boolean";
        if (MODEL_TYPE_HINTS.NUMBER_KEYS.has(k) || MODEL_TYPE_HINTS.NUMBER_KEYS.has(lower)) return "number";
        if (MODEL_TYPE_HINTS.DATE_KEYS.has(k) || MODEL_TYPE_HINTS.DATE_KEYS.has(lower)) {
            // dob is date-only; timestamps are datetime-ish, but we render date-only input for dob specifically
            if (lower === "dob") return "date";
            if (looksLikeISODateOnly(value)) return "date";
            return "text";
        }

        // ✅ Fallbacks based on actual value shape
        if (typeof value === "boolean") return "boolean";
        if (typeof value === "number") return "number";

        if (value instanceof Date) return "text";

        if (typeof value === "string") {
            if (looksLikeISODateOnly(value)) return "date";
            if (looksLikeISODateTime(value)) return "text";
            if (value.length > 120) return "textarea";
            if (/experience|qualification|bio|about|notes|description/i.test(k) && value.length > 60) return "textarea";
            return "text";
        }

        if (value && typeof value === "object") return "json";
        return "text";
    };

    const coerceValueForSave = (type, raw, original) => {
        // ✅ Normalize undefined
        if (raw === undefined) raw = "";

        if (type === "boolean") {
            // UI uses select => "true"/"false"
            if (raw === true || raw === false) return raw;
            if (raw === "true") return true;
            if (raw === "false") return false;

            // If empty, keep original (avoid overwriting boolean with something weird)
            if (raw === "") return original ?? null;

            return Boolean(raw);
        }

        if (type === "number") {
            // empty string means "clear" (set NULL) if originally had value; otherwise treat as null
            if (raw === "") return null;
            const n = Number(raw);
            return Number.isFinite(n) ? n : original ?? null;
        }

        if (type === "date") {
            // HTML date input gives YYYY-MM-DD or ""
            if (raw === "") return null;
            return raw;
        }

        if (type === "json") {
            if (raw === "" || raw === null) return null;
            if (typeof raw === "object") return raw;
            try {
                return JSON.parse(raw);
            } catch {
                // invalid JSON => keep original so we don't corrupt data
                return original ?? null;
            }
        }

        // text / textarea
        return raw;
    };

    const openEdit = async (row) => {
        const { role, id } = resolveTarget(row);

        if (!role || !id || role === "unknown") {
            alert("Cannot edit: missing role or role_id/ID.");
            return;
        }

        try {
            setEditError("");
            setEditLoading(true);
            setEditOpen(true);
            setEditingRow(row);
            setEditRecord(null);
            setEditForm({});

            // ✅ Admin GET endpoints you already have:
            // - Player:   GET /api/admin/player/:id
            // - Roles:    GET /api/admin/:role/:id/profile
            let res;
            if (role === "player") {
                res = await apiFetch(`/api/admin/player/${encodeURIComponent(id)}`, { method: "GET" });
            } else {
                res = await apiFetch(`/api/admin/${encodeURIComponent(role)}/${encodeURIComponent(id)}/profile`, {
                    method: "GET",
                });
            }

            if (!res.ok) throw new Error(await res.text());

            const data = await res.json();
            const record = data || {};

            setEditRecord(record);

            // ✅ init form with *all editable keys* (admin can edit everything except blocked keys)
            const keys = getEditableKeysFromRecord(record);
            const init = {};
            keys.forEach((k) => {
                const v = record?.[k];

                const type = detectFieldType(k, v);

                // json objects => start as formatted JSON string
                if (type === "json") {
                    if (v && typeof v === "object") {
                        try {
                            init[k] = JSON.stringify(v, null, 2);
                        } catch {
                            init[k] = "";
                        }
                    } else {
                        init[k] = v ?? "";
                    }
                    return;
                }

                // boolean => store as "true"/"false" for <select>
                if (type === "boolean") {
                    if (typeof v === "boolean") init[k] = String(v);
                    else if (v === "true" || v === "false") init[k] = v;
                    else init[k] = "false";
                    return;
                }

                // number/date/text => display null as ""
                init[k] = v ?? "";
            });

            setEditForm(init);
        } catch (e) {
            console.error("Admin GET detail failed:", e);
            setEditError("Failed to load editable record.");
        } finally {
            setEditLoading(false);
        }
    };

    const closeEdit = () => {
        setEditOpen(false);
        setEditError("");
        setEditLoading(false);
        setEditSaving(false);
        setEditingRow(null);
        setEditRecord(null);
        setEditForm({});
    };

    const onEditChange = (key, value) => {
        setEditForm((prev) => ({ ...prev, [key]: value }));
    };

    const saveEdit = async () => {
        if (!editingRow) return;

        const { role, id } = resolveTarget(editingRow);
        if (!role || !id) return;

        try {
            setEditSaving(true);
            setEditError("");

            const record = editRecord || {};
            const keys = getEditableKeysFromRecord(record);

            // ✅ Build payload from editForm, coerced to types, and diff-only
            // Fixes:
            // - null is NOT treated as object
            // - null/undefined + "" is treated as unchanged unless user is actually clearing a real value
            // - numbers/dates never send ""
            const payload = {};

            for (const k of keys) {
                const original = record?.[k];
                const nextRaw = editForm?.[k];

                const type = detectFieldType(k, original);

                // ✅ If original is null/undefined and UI is blank string, treat as unchanged
                const originalEmpty = original === null || original === undefined;
                const nextEmptyString = nextRaw === "";
                if (originalEmpty && nextEmptyString) {
                    continue;
                }

                // ✅ Coerce based on model-informed type
                const next = coerceValueForSave(type, nextRaw, original);

                // ✅ Compare safely (null is not object)
                const originalIsObject = original !== null && typeof original === "object";
                const nextIsObject = next !== null && typeof next === "object";

                let same = false;

                if (originalIsObject || nextIsObject) {
                    try {
                        same = JSON.stringify(original ?? null) === JSON.stringify(next ?? null);
                    } catch {
                        same = false;
                    }
                } else {
                    // Normalize for comparison: null/undefined => ""
                    same = String(original ?? "") === String(next ?? "");
                }

                if (!same) payload[k] = next;
            }

            if (Object.keys(payload).length === 0) {
                closeEdit();
                return;
            }

            // ✅ Use your generic endpoint:
            // PUT /api/admin/user-update?table=player|agent|scout|club|allusers&id=123
            const res = await apiFetch(
                `/api/admin/user-update?table=${encodeURIComponent(role)}&id=${encodeURIComponent(id)}`,
                {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ updates: payload }),
                }
            );

            if (!res.ok) throw new Error(await res.text());

            const out = await res.json();
            const updatedRecord = out?.updated ?? out ?? {};

            // Update modal record
            setEditRecord(updatedRecord);

            // Also update list row preview (best-effort, don’t break lookup)
            setRows((prev) =>
                prev.map((r) => {
                    if (r.id !== editingRow.id) return r;

                    const nextRow = { ...r };
                    if (typeof payload.fullName === "string") nextRow.fullName = payload.fullName || null;
                    if (typeof payload.loginEmail === "string") nextRow.loginEmail = payload.loginEmail || null;

                    // username/profileSlug are locked, these are no-ops if never present
                    if (typeof payload.username === "string") nextRow.username = payload.username || null;
                    if (typeof payload.profileSlug === "string") nextRow.profileSlug = payload.profileSlug || null;

                    return nextRow;
                })
            );

            closeEdit();
        } catch (e) {
            console.error("Admin save failed:", e);
            setEditError("Failed to save changes.");
        } finally {
            setEditSaving(false);
        }
    };

    const modalTitle = useMemo(() => {
        if (!editingRow) return "Edit User";
        const { role, id } = resolveTarget(editingRow);
        return `Edit ${String(role || "user").toUpperCase()} (ID: ${id ?? "—"})`;
    }, [editingRow, table]);

    return (
        <div className="UserLookup">
            <div className="UL__container">
                <div className="UL__header">
                    <div className="UL__titleWrap">
                        <span className="UL__lock" aria-hidden="true">
                            🔎
                        </span>
                        <div>
                            <h2 className="UL__title">User Lookup</h2>
                            <p className="UL__subtitle">
                                Search records in <b>{tableLabel}</b> and view details. You can also edit users (admin-only).
                            </p>
                        </div>
                    </div>
                </div>

                {/* Search Controls */}
                <div className="UL__searchCard">
                    <div className="UL__searchRow">
                        <div className="UL__field">
                            <label className="UL__label">Table</label>
                            <select className="UL__select" value={table} onChange={(e) => setTable(e.target.value)}>
                                <option value="allusers">AllUsers (Recommended)</option>
                                <option value="player">PlayerData</option>
                                <option value="agent">AgentData</option>
                                <option value="scout">ScoutData</option>
                                <option value="club">ClubData</option>
                            </select>
                        </div>

                        <div className="UL__field">
                            <label className="UL__label">Search By</label>
                            <select className="UL__select" value={searchBy} onChange={(e) => setSearchBy(e.target.value)}>
                                <option value="all">All (Recommended)</option>
                                <option value="fullName">Full Name</option>
                                <option value="username">Username</option>
                                <option value="profileSlug">Profile Slug</option>
                                <option value="loginEmail">Login Email</option>
                                <option value="id">ID</option>
                            </select>
                        </div>

                        <div className="UL__field UL__field--grow">
                            <label className="UL__label">Search</label>
                            <input
                                className="UL__input"
                                type="text"
                                placeholder={placeholder}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyDown={onKeyDown}
                            />
                        </div>

                        <div className="UL__field" style={{ minWidth: 120 }}>
                            <label className="UL__label">Limit</label>
                            <input
                                className="UL__input"
                                type="number"
                                min={1}
                                max={50}
                                value={limit}
                                onChange={(e) => setLimit(Number(e.target.value || 25))}
                            />
                        </div>

                        <button className="UL__btn UL__btn--primary" onClick={searchUsers} disabled={loading}>
                            {loading ? "Searching..." : "Search"}
                        </button>
                    </div>

                    {error && <p className="UL__error">{error}</p>}

                    <div className="UL__hint">
                        Tip: press <span className="UL__kbd">Enter</span> to search.
                    </div>
                </div>

                {/* Results */}
                <div className="UL__results">
                    {rows.length === 0 && !loading ? (
                        <div className="UL__empty">
                            <div className="UL__emptyTitle">No results yet</div>
                            <div className="UL__emptyText">
                                Search by <b>username</b>, <b>full name</b>, <b>profileSlug</b>, <b>loginEmail</b>, or <b>ID</b>.
                            </div>
                        </div>
                    ) : (
                        rows.map((row) => {
                            const expanded = !!expandedMap[row.id];

                            const previewFields = [
                                { label: "Full Name", value: row.fullName || "N/A" },
                                { label: "Username", value: row.username ? `@${row.username}` : "N/A" },
                                { label: "ID", value: row.id },
                                { label: "Profile Slug", value: row.profileSlug || "N/A" },
                                { label: "Login Email", value: row.loginEmail || "N/A" },
                            ];

                            const pillText =
                                typeof row.isDisabled === "boolean"
                                    ? row.isDisabled
                                        ? "Disabled"
                                        : "Active"
                                    : row.role || tableLabel;

                            const pillClass =
                                typeof row.isDisabled === "boolean"
                                    ? row.isDisabled
                                        ? "UL__pill--no"
                                        : "UL__pill--yes"
                                    : "UL__pill--no";

                            const raw = row.raw || {};
                            const hiddenKeys = new Set([
                                "id",
                                "fullName",
                                "full_name",
                                "username",
                                "profileSlug",
                                "profile_slug",
                                "loginEmail",
                                "login_email",
                            ]);

                            const extraFields = Object.keys(raw)
                                .filter((k) => !hiddenKeys.has(k))
                                .sort((a, b) => a.localeCompare(b))
                                .map((k) => ({ label: k, value: formatValue(raw[k]) }));

                            return (
                                <div key={row.id} className="UL__playerCard">
                                    <div className="UL__playerLeft">
                                        <div className="UL__nameRow">
                                            <span className="UL__name">
                                                {row.fullName || row.username || row.loginEmail || "Unnamed User"}
                                            </span>

                                            <span className={`UL__pill ${pillClass}`}>{pillText}</span>

                                            <button
                                                type="button"
                                                className="UL__detailsBtn"
                                                onClick={() => toggleExpanded(row.id)}
                                                title="View all details"
                                            >
                                                {expanded ? "Hide Details" : "See Details"}
                                            </button>
                                        </div>

                                        <div className="UL__meta">
                                            {previewFields.map((f) => (
                                                <Field key={f.label} label={f.label} value={f.value} />
                                            ))}
                                        </div>

                                        {expanded && (
                                            <div className="UL__detailsWrap">
                                                <div className="UL__detailsTitle">All Details</div>
                                                <div className="UL__meta UL__meta--details">
                                                    {extraFields.length === 0 ? (
                                                        <Field label="Details" value="No additional fields available." />
                                                    ) : (
                                                        extraFields.map((f) => (
                                                            <Field key={f.label} label={f.label} value={f.value} />
                                                        ))
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="UL__actions">
                                        <button className="UL__btn UL__btn--danger" type="button" onClick={() => openEdit(row)}>
                                            Edit User
                                        </button>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            {/* ✅ EDIT MODAL (scrollable via CSS classes) */}
            {editOpen && (
                <div
                    className="UL__modalOverlay"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) closeEdit();
                    }}
                >
                    <div className="UL__modalContent">
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
                            <div>
                                <div style={{ color: "#ffdd00", fontWeight: 900, fontSize: 16 }}>{modalTitle}</div>
                                <div style={{ color: "rgba(255,255,255,0.75)", fontSize: 13, marginTop: 4 }}>
                                    Admin-only: edits are applied to the real role table (PlayerData / AgentData / ScoutData / ClubData).
                                </div>
                            </div>

                            <button type="button" className="UL__btn UL__btn--outline" onClick={closeEdit} disabled={editSaving}>
                                Close
                            </button>
                        </div>

                        {editError && (
                            <div className="UL__error" style={{ marginTop: 12 }}>
                                {editError}
                            </div>
                        )}

                        {editLoading ? (
                            <div style={{ padding: 16, color: "rgba(255,255,255,0.8)" }}>Loading details...</div>
                        ) : (
                            <>
                                <div style={{ marginTop: 12 }}>
                                    <div style={{ fontWeight: 900, marginBottom: 8, color: "rgba(255,255,255,0.9)" }}>
                                        Editable Fields
                                    </div>

                                    <div
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns: "repeat(2, minmax(220px, 1fr))",
                                            gap: 10,
                                        }}
                                    >
                                        {(() => {
                                            const keys = getEditableKeysFromRecord(editRecord || {});
                                            if (keys.length === 0) {
                                                return (
                                                    <div className="UL__metaItem">
                                                        <div className="UL__metaLabel">No editable fields</div>
                                                        <div className="UL__metaValue">All fields are locked or unavailable.</div>
                                                    </div>
                                                );
                                            }

                                            return keys.map((key) => {
                                                const original = editRecord?.[key];
                                                const type = detectFieldType(key, original);
                                                const value = editForm?.[key] ?? "";

                                                if (type === "boolean") {
                                                    return (
                                                        <div key={key} className="UL__metaItem">
                                                            <div className="UL__metaLabel">{humanizeKey(key)}</div>
                                                            <select
                                                                className="UL__select"
                                                                value={String(value)}
                                                                onChange={(e) => onEditChange(key, e.target.value)}
                                                            >
                                                                <option value="true">true</option>
                                                                <option value="false">false</option>
                                                            </select>
                                                        </div>
                                                    );
                                                }

                                                if (type === "number") {
                                                    return (
                                                        <div key={key} className="UL__metaItem">
                                                            <div className="UL__metaLabel">{humanizeKey(key)}</div>
                                                            <input
                                                                className="UL__input"
                                                                type="number"
                                                                value={value}
                                                                onChange={(e) => onEditChange(key, e.target.value)}
                                                                placeholder={humanizeKey(key)}
                                                            />
                                                        </div>
                                                    );
                                                }

                                                if (type === "date") {
                                                    return (
                                                        <div key={key} className="UL__metaItem">
                                                            <div className="UL__metaLabel">{humanizeKey(key)}</div>
                                                            <input
                                                                className="UL__input"
                                                                type="date"
                                                                value={value}
                                                                onChange={(e) => onEditChange(key, e.target.value)}
                                                            />
                                                        </div>
                                                    );
                                                }

                                                if (type === "json") {
                                                    return (
                                                        <div key={key} className="UL__metaItem">
                                                            <div className="UL__metaLabel">{humanizeKey(key)} (JSON)</div>
                                                            <textarea
                                                                className="UL__input"
                                                                style={{ height: 120, paddingTop: 10, paddingBottom: 10, resize: "vertical" }}
                                                                value={value}
                                                                onChange={(e) => onEditChange(key, e.target.value)}
                                                                placeholder='{"key":"value"}'
                                                            />
                                                        </div>
                                                    );
                                                }

                                                if (type === "textarea") {
                                                    return (
                                                        <div key={key} className="UL__metaItem">
                                                            <div className="UL__metaLabel">{humanizeKey(key)}</div>
                                                            <textarea
                                                                className="UL__input"
                                                                style={{ height: 120, paddingTop: 10, paddingBottom: 10, resize: "vertical" }}
                                                                value={value}
                                                                onChange={(e) => onEditChange(key, e.target.value)}
                                                                placeholder={humanizeKey(key)}
                                                            />
                                                        </div>
                                                    );
                                                }

                                                return (
                                                    <div key={key} className="UL__metaItem">
                                                        <div className="UL__metaLabel">{humanizeKey(key)}</div>
                                                        <input
                                                            className="UL__input"
                                                            value={value}
                                                            onChange={(e) => onEditChange(key, e.target.value)}
                                                            placeholder={humanizeKey(key)}
                                                        />
                                                    </div>
                                                );
                                            });
                                        })()}
                                    </div>
                                </div>

                                <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 14 }}>
                                    <button type="button" className="UL__btn UL__btn--outline" onClick={closeEdit} disabled={editSaving}>
                                        Cancel
                                    </button>
                                    <button type="button" className="UL__btn UL__btn--primary" onClick={saveEdit} disabled={editSaving}>
                                        {editSaving ? "Saving..." : "Save Changes"}
                                    </button>
                                </div>

                                <div style={{ marginTop: 14 }}>
                                    <div style={{ fontWeight: 900, marginBottom: 8, color: "rgba(255,255,255,0.9)" }}>
                                        Current Full Record (Read-only)
                                    </div>
                                    <div
                                        style={{
                                            background: "#000",
                                            border: "1px solid #1f1f1f",
                                            borderRadius: 12,
                                            padding: 12,
                                            maxHeight: 220,
                                            overflow: "auto",
                                            fontFamily: "monospace",
                                            fontSize: 12,
                                            color: "rgba(255,255,255,0.85)",
                                        }}
                                    >
                                        {editRecord ? JSON.stringify(editRecord, null, 2) : "No record loaded."}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserLookup;